import { IconButton, css, styled } from "@mui/material";

interface StyledIconButtonProps {
  iconButtonType?: "warning" | "primary" | "secondary" | "UNSAFE_transparent";
  iconButtonVariant?: "full" | "transparent";
  shape?: "square" | "round";
  iconButtonSize?: "small" | "medium" | "big";
  isSelected?: boolean;
}

export const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== "iconButtonType" &&
    prop !== "iconButtonSize" &&
    prop !== "iconButtonVariant" &&
    prop !== "isSelected",
})<StyledIconButtonProps>`
  color: ${(props) =>
    props.iconButtonType === "primary"
      ? props.theme.wsc.palette.primary.contrastText
      : props.theme.wsc.palette.neutral[100]};
  transition: all 0.15s;

  &&:focus {
    outline: 0;
  }

  &:disabled {
    color: ${(props) => props.theme.wsc.palette.neutral[300]};
  }

  &&& {
    border-radius: ${({ theme, shape }) =>
      theme.wsc.radius[shape === "round" ? "half" : "sm"]};
  }

  ${(props) =>
    props.iconButtonSize === "small" &&
    `
        width: ${props.theme.wsc.size.m};
        height: ${props.theme.wsc.size.m};
    `}

  ${(props) =>
    props.iconButtonSize === "medium" &&
    `
        width: ${props.theme.wsc.size.l};
        height: ${props.theme.wsc.size.l};
    `}

  ${(props) =>
    props.iconButtonSize === "big" &&
    `
        width: ${props.theme.wsc.size.xl};
        height: ${props.theme.wsc.size.xl};
    `}

  ${(props) => {
    switch (props.iconButtonType) {
      case "primary":
        return css`
          ${props.iconButtonVariant === "full" &&
          `
                        background-color: ${
                          props.theme.wsc.palette.primary[
                            props.isSelected ? 500 : 300
                          ]
                        };
                        
                        :hover {
                            background-color: ${
                              props.theme.wsc.palette.primary[100]
                            };
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.primary[500]
                            };
                        }
                        
                        :disabled {
                            color: ${props.theme.wsc.palette.neutral[400]};
                            background-color: ${
                              props.theme.wsc.palette.primary[700]
                            };
                        }
                    `}

          ${props.iconButtonVariant === "transparent" &&
          `
                        background-color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.primary[300]
                            : "transparent"
                        };
                        color: ${
                          props.theme.wsc.palette.primary[
                            props.isSelected ? "contrastText" : 300
                          ]
                        };

                        :hover {
                            background-color: ${
                              props.theme.wsc.palette.primary[100]
                            };
                            color: ${
                              props.theme.wsc.palette.primary.contrastText
                            };
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.primary[300]
                            };
                            color: ${
                              props.theme.wsc.palette.primary.contrastText
                            };
                        }
                        
                        :disabled {
                            color: ${props.theme.wsc.palette.primary[700]};
                        }
                    `}
        `;
      case "warning":
        return css`
          ${props.iconButtonVariant === "full" &&
          `
                        background-color: ${
                          props.theme.wsc.palette.warning[
                            props.isSelected ? 500 : 300
                          ]
                        };
                        
                        :hover {
                            background-color: ${
                              props.theme.wsc.palette.warning[100]
                            };
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.warning[500]
                            };
                        }
                        
                        :disabled {
                            background-color: ${
                              props.theme.wsc.palette.warning[700]
                            };
                        }
                    `}

          ${props.iconButtonVariant === "transparent" &&
          `
                        background-color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.warning[500]
                            : "transparent"
                        };
                        color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.neutral[100]
                            : props.theme.wsc.palette.warning[300]
                        };
                        
                        :hover {
                            background-color: ${
                              props.theme.wsc.palette.warning[100]
                            };
                            color: ${props.theme.wsc.palette.neutral[100]};
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.warning[500]
                            };
                            color: ${props.theme.wsc.palette.neutral[100]};
                        }
                        
                        :disabled {
                            color: ${props.theme.wsc.palette.warning[700]};
                        }
                    `}
        `;
      case "secondary":
        return css`
          ${props.iconButtonVariant === "full" &&
          `
                        background-color: ${
                          props.theme.wsc.palette.neutral[
                            props.isSelected ? 500 : 700
                          ]
                        };
                        color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.primary[500]
                            : props.theme.wsc.palette.neutral[100]
                        };

                        :hover {
                            color: ${props.theme.wsc.palette.neutral[200]};
                            background-color: ${
                              props.theme.wsc.palette.neutral[600]
                            };
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.neutral[500]
                            };
                            color: ${props.theme.wsc.palette.primary[500]};
                        }
                        
                        :disabled {
                            background-color: transparent;
                            color: ${props.theme.wsc.palette.neutral[400]};
                        }
                    `}

          ${props.iconButtonVariant === "transparent" &&
          `
                        background-color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.neutral[500]
                            : "transparent"
                        };
                        color: ${
                          props.isSelected
                            ? props.theme.wsc.palette.primary[500]
                            : props.theme.wsc.palette.neutral[100]
                        };
                        
                        :hover {
                            background-color: ${
                              props.theme.wsc.palette.neutral[600]
                            };
                        }

                        :active {
                            background-color: ${
                              props.theme.wsc.palette.neutral[500]
                            };
                            color: ${props.theme.wsc.palette.primary[500]};
                        }
                        
                        :disabled {
                            color: ${props.theme.wsc.palette.neutral[400]};
                        }
                    `}
        `;
      case "UNSAFE_transparent":
        return css`
          background-color: transparent;
        `;
    }
  }}
`;
